import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Progress } from '../ui/progress';
import { ChevronLeft, ChevronRight, Check, AlertCircle, Save } from 'lucide-react';

const FormShell = ({
  title,
  stepLabels,
  currentStep,
  onNext,
  onPrevious,
  onSubmit,
  children,
  autoSaved,
}) => {
  const totalSteps = stepLabels.length;
  const progress = (currentStep / totalSteps) * 100;
  const isLast = currentStep === totalSteps;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="service-form-title">
            {title}
          </h1>
          <div className="flex items-center gap-3">
            {autoSaved && (
              <span
                className="text-xs text-emerald-700 flex items-center gap-1"
                data-testid="autosave-indicator"
              >
                <Save className="h-3 w-3" /> Draft saved
              </span>
            )}
            <span className="text-sm text-gray-600">
              Step {currentStep} of {totalSteps}
            </span>
          </div>
        </div>
        <Progress value={progress} className="h-2" />

        <div className="flex justify-between mt-4">
          {stepLabels.map((step, index) => {
            const n = index + 1;
            const completed = n < currentStep;
            const active = n === currentStep;
            return (
              <div key={step} className="flex flex-col items-center flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    completed || active
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  {completed ? <Check className="h-5 w-5" /> : n}
                </div>
                <span className="text-xs mt-1 text-gray-600 hidden md:block text-center">
                  {step}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">{children}</CardContent>
      </Card>

      <div className="flex justify-between mt-6">
        <Button
          onClick={onPrevious}
          disabled={currentStep === 1}
          variant="outline"
          className="flex items-center gap-2"
          data-testid="form-previous-btn"
        >
          <ChevronLeft className="h-4 w-4" /> Previous
        </Button>

        {isLast ? (
          <Button
            onClick={onSubmit}
            className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2"
            data-testid="form-submit-btn"
          >
            Proceed to Payment <ChevronRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            onClick={onNext}
            className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2"
            data-testid="form-next-btn"
          >
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900">Need Help?</p>
            <p className="text-sm text-blue-800">
              Contact us at <strong>hello@consultiumgroup.co.za</strong>, call{' '}
              <strong>012 824 3708</strong> or WhatsApp{' '}
              <strong>079 042 4911</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormShell;
